import { inject, Injectable } from '@angular/core';
import { AuthStoreSelector } from '@common/selectors/authentication.selector';
import { CartStoreSelector } from '@common/selectors/cart.selector';
import { OrderStoreSelector } from '@common/selectors/order.selector';
import { ShopStoreSelector } from '@common/selectors/shop.selector';
import { combineLatest, map, Observable, shareReplay, interval, startWith } from 'rxjs';
import { ExpressionLanguageUtile } from './expression-language.utile';
import { DateUtils } from '@shared/utils/date/date.utils';
import { ComoService } from '@core/services/como/como.service';
import { ZerosixService } from '@core/services/zerosix/zerosix.service';
import { PongoService } from '@core/services/pongo/pongo.service';

@Injectable({ providedIn: 'root' })
export class EvaluateBlockVisibilityService {
  private readonly cartStoreSelector = inject(CartStoreSelector);
  private readonly authStoreSelector = inject(AuthStoreSelector);
  private readonly orderStoreSelector = inject(OrderStoreSelector);
  private readonly shopStoreSelector = inject(ShopStoreSelector);
  private readonly comoService = inject(ComoService);
  private readonly zerosixService = inject(ZerosixService);
  private readonly pongoService = inject(PongoService);

  // Create a time observable that starts immediately and updates every minute
  private readonly time$ = interval(60000).pipe(
    startWith(0), // Start immediately
    map(() => DateUtils.dayjsInMarketplaceTimezone().toDate()),
    shareReplay(1),
  );

  // Create a loyalty membership observable that checks if user is member of any loyalty program
  private readonly isLoyaltyMember$ = combineLatest({
    isComoMember: this.comoService.isUserComoAccountEnabledOnMarketplaceLevel$(),
    isZerosixMember: this.zerosixService.isUserZeroSixCardEnabledOnMarketplaceLevel$(),
    isPongoMember: this.pongoService.isUserPongoCustomerEnabledOnMarketplaceLevel$(),
  }).pipe(
    map(
      ({ isComoMember, isZerosixMember, isPongoMember }) =>
        isComoMember || isZerosixMember || isPongoMember,
    ),
    shareReplay(1),
  );

  private readonly context$ = combineLatest({
    cart: this.cartStoreSelector.selectActive,
    user: this.authStoreSelector.selectUser,
    order: this.orderStoreSelector.selectValidation,
    shop: this.shopStoreSelector.selectActive,
    time: this.time$,
    isLoyaltyMember: this.isLoyaltyMember$,
  }).pipe(shareReplay(1));

  evaluate$(expression: string): Observable<boolean> {
    return this.context$.pipe(
      map(context => {
        const result = ExpressionLanguageUtile.evaluate(expression, context);
        if (result === null) {
          console.error('Expression did not evaluate to a boolean:', expression, result);
          // Always return true if the expression is invalid
          return true;
        }
        return result;
      }),
    );
  }
}
